'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { ArrowRight } from 'lucide-react';
import { CarIllustration } from './CarIllustrations';
import { useLocale } from '@/lib/i18n';
import type { HeroLayout } from '@/lib/storefront/appearance';

const FALLBACK_ROAD = 'https://images.unsplash.com/photo-1506015391300-4802dc74de2e?auto=format&fit=crop&w=1920&q=80';

function publicImage(url: string | undefined, fallback: string): string {
  const value = url?.trim() ?? '';
  if (value.startsWith('/') || value.startsWith('https://') || value.startsWith('http://')) return value;
  return fallback;
}

interface HeroSectionProps {
  onBookRide: () => void;
  headline?: string;
  description?: string;
  ctaLabel?: string;
  imageUrl?: string;
  /** Chosen in the console's appearance editor. */
  layout?: HeroLayout;
}

export function HeroSection({
  onBookRide,
  headline,
  description,
  ctaLabel,
  imageUrl,
  layout = 'full-bleed',
}: HeroSectionProps) {
  const { t } = useLocale();
  const [carImageError, setCarImageError] = useState(false);
  const [bgImageError, setBgImageError] = useState(false);

  const centered = layout === 'centered';
  const imageFirst = layout === 'image-left';
  const carSource = publicImage(
    imageUrl,
    'https://images.unsplash.com/photo-1555215695-3004980ad54e?auto=format&fit=crop&w=1000&q=80',
  );

  const headlineBlock = (
    <h1
      className={`text-[28px] sm:text-5xl lg:text-[54px] font-extrabold text-white leading-[1.12] tracking-tight mb-4 break-words max-w-full ${
        centered ? 'text-center' : ''
      }`}
    >
      {headline || (
        <>
          {t('hero.titleA')} <br />
          <span className="text-white">{t('hero.titleB')} </span>
          <span className="text-[var(--color-primary)]">{t('hero.titleC')}</span>
        </>
      )}
    </h1>
  );

  const descriptionBlock = (
    <p className={`text-sm sm:text-base text-slate-300 leading-relaxed max-w-lg mb-8 ${centered ? 'mx-auto text-center' : ''}`}>
      {description || t('hero.sub')}
    </p>
  );

  const ctaBlock = (
    <button
      type="button"
      onClick={onBookRide}
      className="group inline-flex items-center gap-3 px-7 py-3.5 bg-[var(--color-primary)] hover:bg-[var(--color-primary-dark)] active:scale-95 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-lg shadow-orange-950/40 transition-all cursor-pointer"
    >
      <span>{ctaLabel || t('hero.cta')}</span>
      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
    </button>
  );

  const carBlock = (
    <div className="relative mb-6 w-full max-w-[560px] transform transition-transform duration-500 hover:scale-[1.02] lg:mb-10">
      {!carImageError ? (
        <div className="relative w-full h-[220px] sm:h-[340px] rounded-2xl overflow-hidden shadow-2xl border border-white/10 group">
          <Image
            src={carSource}
            alt="fleetava Executive Luxury BMW Sedan"
            fill
            className="object-cover object-center group-hover:scale-105 transition-transform duration-700"
            referrerPolicy="no-referrer"
            onError={() => setCarImageError(true)}
          />
          {/* Subtle rim highlight & badge */}
          <div className="absolute bottom-3 left-4 right-4 sm:right-auto px-3 py-1 bg-black/60 backdrop-blur-md rounded-lg border border-white/10 text-white text-xs font-semibold flex items-center gap-2 max-w-full">
            <span className="w-2 h-2 rounded-full bg-[var(--color-primary)] shrink-0" />
            <span className="truncate">{t('hero.badge')}</span>
          </div>
        </div>
      ) : (
        <CarIllustration type="bmw_5series" className="w-full h-auto drop-shadow-2xl" />
      )}

      {/* Speed road reflection glow */}
      <div className="absolute -bottom-2 left-1/4 right-1/4 h-2 bg-amber-500/20 blur-md rounded-full pointer-events-none" />
    </div>
  );

  return (
    <section className="relative w-full overflow-hidden bg-slate-950 min-h-[520px] lg:min-h-[580px] flex items-center">
      {/* 1. Cinematic Background: Mountain Highway Road Landscape Photo */}
      <div className="absolute inset-0 z-0">
        {!bgImageError ? (
          <Image
            src={FALLBACK_ROAD}
            alt="Scenic Mountain Road Highway"
            fill
            priority
            className="object-cover object-center scale-105 filter brightness-[0.7] contrast-[1.05]"
            referrerPolicy="no-referrer"
            onError={() => setBgImageError(true)}
          />
        ) : null}

        {/* Cinematic Vignette Overlay Scrim for Legibility (from reference image) */}
        {centered ? (
          <div className="absolute inset-0 bg-[#090d14]/70" />
        ) : (
          <div
            className={`absolute inset-0 ${
              imageFirst
                ? 'bg-gradient-to-l from-[#090d14] via-[#090d14]/75 to-transparent'
                : 'bg-gradient-to-r from-[#090d14] via-[#090d14]/75 to-transparent'
            }`}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#090d14] via-transparent to-transparent opacity-90" />
      </div>

      {/* 2. Hero Content Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 w-full pt-12 pb-28 lg:pb-36">
        {centered ? (
          <div className="mx-auto flex max-w-3xl flex-col items-center min-w-0">
            {headlineBlock}
            {descriptionBlock}
            {ctaBlock}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Text Column (Span 6) */}
            <div className={`lg:col-span-6 flex flex-col items-start max-w-xl min-w-0 ${imageFirst ? 'lg:order-2' : ''}`}>
              {headlineBlock}
              {descriptionBlock}
              {ctaBlock}
            </div>

            {/* Hero Luxury Car Photo (Span 6) */}
            <div className={`lg:col-span-6 flex items-center justify-center relative mt-6 lg:mt-0 ${imageFirst ? 'lg:order-1' : ''}`}>
              {carBlock}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
